"use client";

import { tileImages } from "@/lib/constants";
import { cn, getRandomNumber, shuffleArray } from "@/lib/utils";
import { ReactNode, useEffect, useRef } from "react";
import "./vision-board-design.css";
import { useTheme } from "next-themes";
import { useVisionBoardPreferences } from "@/hooks/use-board";

export default function VisionBoardDesign({
  children,
}: {
  children: ReactNode;
}) {
  const { resolvedTheme } = useTheme();
  const { isVisible, getColorForTile } = useVisionBoardPreferences();
  const hasInitialized = useRef(false);

  useEffect(() => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    const columns = Math.floor(screenWidth / 50);
    const rows = Math.floor(screenHeight / 50);

    const shuffledTileImages = shuffleArray([...tileImages]);

    const tilesElement = document.getElementById("tiles");

    if (tilesElement && !hasInitialized.current) {
      createGrid({
        resolvedTheme: resolvedTheme || "dark",
        tiles: tilesElement,
        columns,
        rows,
        shuffledTileImages,
        getColorForTile,
      });
      hasInitialized.current = true;
    }
  }, [resolvedTheme, isVisible, getColorForTile]);

  // Handle visibility changes with instant transition
  useEffect(() => {
    const tilesElement = document.getElementById("tiles");
    if (!tilesElement) return;

    // Apply or remove the hidden class instantly
    if (isVisible) {
      tilesElement.classList.remove("hide");
    } else {
      tilesElement.classList.add("hide");
    }
  }, [isVisible]);

  // Update tile colors when theme changes
  useEffect(() => {
    const tilesElement = document.getElementById("tiles");
    if (!tilesElement) return;

    const tiles = tilesElement.querySelectorAll(".tile");
    tiles.forEach((tile, index) => {
      const htmlTile = tile as HTMLDivElement;
      const theme = resolvedTheme === "dark" ? "dark" : "light";
      const { color } = getColorForTile(index, theme);
      htmlTile.style.backgroundColor = color;
    });
  }, [resolvedTheme, getColorForTile]);

  return (
    <>
      <div
        className={cn(
          "fixed top-0 left-0 h-screen w-screen overflow-hidden grid -z-10",
          !isVisible && "hide",
        )}
        id="tiles"
      />
      <div className="fixed top-0 left-0 h-screen w-screen z-50 pointer-events-none">
        <div className="pointer-events-auto h-full w-full transition-all">
          {children}
        </div>
      </div>
    </>
  );
}

export const createTile = (
  resolvedTheme: string,
  index: number,
  getColorForTile: (
    tileIndex: number,
    theme: "light" | "dark",
  ) => { color: string; colorIndex: number },
): HTMLDivElement => {
  const tile = document.createElement("div");
  tile.id = `tile-${index}`;
  tile.classList.add("tile");

  const theme = resolvedTheme === "dark" ? "dark" : "light";
  const { color } = getColorForTile(index, theme);

  tile.style.backgroundColor = color;

  return tile;
};

export function createTiles(
  resolvedTheme: string,
  tiles: HTMLElement,
  tileQuantity: number,
  shuffledImages: string[],
  getColorForTile: (
    tileIndex: number,
    theme: "light" | "dark",
  ) => { color: string; colorIndex: number },
) {
  for (let index = 0; index < tileQuantity; index++) {
    if (index >= shuffledImages.length) break;

    const tile = createTile(resolvedTheme, index, getColorForTile);
    const tileImage = document.createElement("img");

    // Correct path for Next.js public folder
    tileImage.src = `/images/${shuffledImages[index]}`;
    tileImage.alt = `Vision board tile ${index}`;

    tileImage.style.zIndex = String(getRandomNumber(8, 15));
    tileImage.style.borderRadius = `${getRandomNumber(0, 12)}px`;

    // Allow images to overflow and overlap - increased range for positioning
    tileImage.style.top = `${getRandomNumber(-20, 20)}%`;
    tileImage.style.left = `${getRandomNumber(-20, 20)}%`;

    // Randomize size with larger variations for more overlap
    const sizeVariation = getRandomNumber(90, 130);
    if (getRandomNumber(1, 2) === 1) {
      tileImage.style.height = `${sizeVariation}%`;
      tileImage.style.width = "auto";
    } else {
      tileImage.style.width = `${sizeVariation}%`;
      tileImage.style.height = "auto";
    }

    tileImage.draggable = false;
    tileImage.style.pointerEvents = "none";

    tile.appendChild(tileImage);
    tiles.appendChild(tile);
  }
}

export const createGrid = ({
  resolvedTheme,
  tiles,
  columns,
  rows,
  shuffledTileImages,
  getColorForTile,
}: {
  resolvedTheme: string;
  tiles: HTMLElement;
  columns: number;
  rows: number;
  shuffledTileImages: string[];
  getColorForTile: (
    tileIndex: number,
    theme: "light" | "dark",
  ) => { color: string; colorIndex: number };
}) => {
  tiles.style.setProperty("--columns", String(columns));
  tiles.style.setProperty("--rows", String(rows));

  createTiles(
    resolvedTheme,
    tiles,
    columns * rows,
    shuffledTileImages,
    getColorForTile,
  );
};
